import { memo, useCallback } from 'react'

import { Save, Warning } from '@mui/icons-material'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { ConfirmButton } from '@shared/components/Confirm/ConfirmButton'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { DialogFormContent } from '@shared/layouts'
import { DurationField } from 'components/DurationField'
import { useVideoStreamUpdateMutation } from 'hooks/mutations/content'

type FormStructure = {
  deleteAfter: number
  isAutodelete: boolean
}

type LifecyclePolicyDialogProps = {
  streamData: VideoStream | VideoStream[]
  onSuccess?: () => void
}

const LifecyclePolicyDialogRaw = ({ streamData, onSuccess }: LifecyclePolicyDialogProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const videoStreamUpdateMutation = useVideoStreamUpdateMutation()
  const { mutate: updateVideoStream } = videoStreamUpdateMutation

  const isArray = Array.isArray(streamData)
  const count = isArray ? streamData.length : 1

  const { handleSubmit, control, watch } = useForm<FormStructure>({
    defaultValues: {
      deleteAfter: isArray ? 0 : streamData.contentLifecyclePolicy.deleteAfter || 0,
      isAutodelete: isArray ? false : !!streamData.contentLifecyclePolicy.deleteAfter,
    },
  })

  const isAutodelete = watch('isAutodelete')

  const handleFormSubmit = useCallback(
    async (formData: FormStructure) => {
      const deleteAfter = formData.isAutodelete ? formData.deleteAfter : 0

      try {
        if (isArray) {
          await Promise.allSettled(
            streamData.map((stream) =>
              updateVideoStream({
                streamId: stream.id,
                videoStreamEdit: {
                  ...stream,
                  contentLifecyclePolicy: {
                    deleteAfter,
                  },
                },
              }),
            ),
          )
        } else {
          await updateVideoStream({
            streamId: streamData.id,
            videoStreamEdit: {
              ...streamData,
              contentLifecyclePolicy: {
                deleteAfter,
              },
            },
          })
        }
        enqueueSnackbar(t('domain:Workspace.Video.updateContentLifecyclePolicySuccess', { count }), {
          variant: 'success',
        })

        onSuccess?.()
        handleClose()
      } catch (_) {
        enqueueSnackbar(t('domain:Workspace.Video.updateContentLifecyclePolicyError', { count }), { variant: 'error' })
      }
    },
    [count, enqueueSnackbar, handleClose, isArray, onSuccess, streamData, t, updateVideoStream],
  )

  return (
    <DialogFormContent
      Illustration={null}
      title={t('domain:Workspace.Video.updateContentLifecyclePolicy', { count })}
      SaveButton={
        <ConfirmButton
          sx={{ ml: 'auto' }}
          startIcon={<Save />}
          variant="text"
          onConfirm={handleSubmit(handleFormSubmit)}
          staticLabel={t('save')}
        />
      }
    >
      <Controller
        name="isAutodelete"
        control={control}
        render={({ field }) => (
          <ToggleButtonGroup value={field.value} color="primary" size="small" sx={{ mb: 4 }}>
            <ToggleButton value={false} onClick={() => field.onChange(false)} sx={{ textTransform: 'capitalize' }}>
              {t('domain:Workspace.Video.noExpiration')}
            </ToggleButton>
            <ToggleButton value onClick={() => field.onChange(true)} sx={{ textTransform: 'capitalize' }}>
              {t('domain:Workspace.Video.autoDeletion')}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      />
      <Controller
        name="deleteAfter"
        control={control}
        render={({ field }) => (
          <DurationField disabled={!isAutodelete} defaultValue={field.value} {...field} label={t('deleteAfter')} />
        )}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: '480px', mt: 5 }}>
        <Typography>{t('domain:Workspace.Video.updatePolicyDescription')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <Warning color="warning" />
          <Typography>
            <Trans ns="domain" i18nKey="Workspace.Video.updatePolicyWarning" />
          </Typography>
        </Box>
      </Box>
    </DialogFormContent>
  )
}

export const LifecyclePolicyDialog = memo(LifecyclePolicyDialogRaw)
